import type { ReactNode } from 'react'
import { Link } from 'react-router-dom'
import { useAuthStore } from '../../stores/authStore'
import { SocMark } from '../soc/SocShell'
import { AlarmBell } from './AlarmBell'

// The SOC lead console's frame (BRIEF-F1b): the customer page's brand (navy
// header, brass mark, cream page; Liter headings, Quattrocento Sans body),
// with the staff sections and the alarm bell.

const navLink = 'inline-flex min-h-[44px] items-center text-[#d9cdb8] no-underline hover:text-white'

export function ConsoleShell({
  alarmCount,
  children,
}: {
  alarmCount: number | null
  children: ReactNode
}) {
  const principal = useAuthStore((s) => s.principal)
  const logout = useAuthStore((s) => s.logout)
  return (
    <div className="min-h-screen flex flex-col bg-cream font-sans text-navy">
      <header className="bg-navy text-cream">
        <div className="mx-auto flex max-w-[1200px] flex-wrap items-center gap-x-8 gap-y-3 px-6 py-4">
          <Link to="/console" className="flex items-center gap-3 text-cream no-underline">
            <SocMark />
            <span className="font-serif text-2xl tracking-wide">DigitalWatch</span>
            <span className="text-sm text-[#d9cdb8]">SOC lead console</span>
          </Link>
          <nav aria-label="Console sections" className="flex grow flex-wrap gap-x-6 text-[15px]">
            <Link to="/console#businesses" className={navLink}>
              Businesses
            </Link>
            <Link to="/console#waiting" className={navLink}>
              Waiting
            </Link>
            <Link to="/console#alarms" className={navLink}>
              Alarms
            </Link>
            <Link to="/analyst" className={navLink}>
              Analyst pages
            </Link>
          </nav>
          <div className="flex items-center gap-4 text-sm text-[#d9cdb8]">
            <AlarmBell count={alarmCount} />
            {principal?.display_name && <span>{principal.display_name}</span>}
            <button
              type="button"
              onClick={logout}
              className="min-h-[44px] cursor-pointer text-[#d9cdb8] hover:text-white"
            >
              Sign out
            </button>
          </div>
        </div>
      </header>
      <main className="mx-auto box-border flex w-full max-w-[1200px] flex-col gap-9 px-4 pb-16 pt-9 sm:px-6">
        {children}
      </main>
      <footer className="border-t border-[#e6dfd2] px-6 py-4 text-center text-[13px] text-[#6b5a3e]">
        DigitalWatch by OSELAB · staff only
      </footer>
    </div>
  )
}

export function SectionMessage({ children, tone = 'plain' }: { children: ReactNode; tone?: 'plain' | 'error' }) {
  return (
    <p
      role={tone === 'error' ? 'alert' : undefined}
      className={
        tone === 'error'
          ? 'm-0 rounded-[10px] border border-danger bg-danger-light px-5 py-3.5 text-[15px] text-danger'
          : 'm-0 rounded-[10px] border border-[#e6dfd2] bg-white px-5 py-3.5 text-[15px] text-[#6b5a3e]'
      }
    >
      {children}
    </p>
  )
}
